import { Button } from '@heidi/design-system/button';
import Document from '@tiptap/extension-document';
import History from '@tiptap/extension-history';
import Paragraph from '@tiptap/extension-paragraph';
import Placeholder from '@tiptap/extension-placeholder';
import Text from '@tiptap/extension-text';
import { EditorContent, useEditor } from '@tiptap/react';
import { ArrowUp } from 'lucide-react';
import { AnimatePresence } from 'motion/react';
import * as m from 'motion/react-m';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { inlineEditBarMessages as messages } from './inline-edit-bar.messages';

// Mirrors the floating Ask Heidi input's ProseMirror styling (vertically
// centred placeholder, no outline, capped height).
const EDITOR_CLASS =
  'w-full min-w-0 [&_.ProseMirror]:max-h-[200px] [&_.ProseMirror]:min-w-0 [&_.ProseMirror]:overflow-y-auto [&_.ProseMirror]:break-words [&_.ProseMirror]:outline-none [&_.ProseMirror_p.is-editor-empty:first-child]:relative [&_.ProseMirror_p.is-editor-empty:first-child]:w-full [&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none [&_.ProseMirror_p.is-editor-empty:first-child::before]:absolute [&_.ProseMirror_p.is-editor-empty:first-child::before]:left-0 [&_.ProseMirror_p.is-editor-empty:first-child::before]:right-0 [&_.ProseMirror_p.is-editor-empty:first-child::before]:top-0 [&_.ProseMirror_p.is-editor-empty:first-child::before]:truncate [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-text-tertiary [&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]';

/**
 * Lite ask-anything prompt shown directly under the highlighted text (v5):
 * full-width input with placeholder and send. Dictation is not wired here.
 */
export const InlineEditPromptInput = ({
  onSubmit,
}: {
  onSubmit: (question: string) => void;
}) => {
  const intl = useIntl();
  const [hasText, setHasText] = useState(false);

  const editor = useEditor({
    extensions: [
      Document,
      Paragraph,
      Text,
      History,
      Placeholder.configure({
        placeholder: intl.formatMessage(messages.promptPlaceholder),
        showOnlyWhenEditable: true,
      }),
    ],
    editorProps: {
      attributes: {
        class: 'outline-none w-full min-w-0 text-base text-text-primary',
      },
    },
    onUpdate: ({ editor: e }) => setHasText(e.getText().trim().length > 0),
    autofocus: 'end',
    immediatelyRender: false,
  });

  const submit = () => {
    const question = editor?.getText().trim();
    if (question) onSubmit(question);
  };

  return (
    <div
      className="flex w-full items-center gap-2"
      // Allow default mousedown behaviour so the editor can take focus (the
      // bar's root prevents it to protect the note selection).
      onMouseDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          submit();
        }
      }}
      data-testid="inline-edit-prompt-input"
    >
      <div className="flex min-h-[32px] min-w-0 flex-1 items-center">
        <EditorContent editor={editor} className={EDITOR_CLASS} />
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <AnimatePresence mode="popLayout" initial={false}>
          {hasText ? (
            <m.div
              key="send"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
            >
              <Button
                type="button"
                variant="default"
                size="sm"
                iconOnly
                onClick={submit}
                aria-label={intl.formatMessage(messages.send)}
                data-testid="inline-edit-prompt-send"
                className="bg-background-primary text-text-primary-invert"
              >
                <ArrowUp size={12} strokeWidth={2.5} />
              </Button>
            </m.div>
          ) : null}
        </AnimatePresence>
      </div>
    </div>
  );
};
